import { SuggestionMessage } from './../model/sugestion-message';
import { Injectable, Self } from '@angular/core';
import { HttpService } from './http.service';
import { Observable } from 'rxjs';
import { Api } from '../enum/Api';
import { SelfAttendanceData } from '../model/self-attendance-data';
import { map } from 'rxjs/operators';
import { SilentOrderService } from './silent-order.service';
import { ShopWindowItem } from "../model/shop-window-item";
import { CreditCardData } from '../model/credit-card-data';

@Injectable()
export class SelfAttendanceService {

  selfAttendanceData: SelfAttendanceData;
  redirectURL: string;
  selectedPackage: ShopWindowItem;

  constructor(
    private httpService: HttpService,
    private silentOrderService: SilentOrderService
  ) {
  }

  getCurrentSelfAttendanceData(): SelfAttendanceData {
    return this.selfAttendanceData;
  }

  clearData() {
    sessionStorage.removeItem('selfAttendanceData');
    sessionStorage.removeItem('name');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('redirectURL');
  }

  getSelfAttendanceDataFromSessionStorage() {
    let selfAttendanceDataStore = sessionStorage.getItem('selfAttendanceData');
    let redirectURLStore = sessionStorage.getItem('redirectURL');
    if (!selfAttendanceDataStore) {
      selfAttendanceDataStore = localStorage.getItem('selfAttendanceData');
    }
    if (!redirectURLStore) {
      redirectURLStore = localStorage.getItem('redirectURL');
    }
    this.setCurrentSelfAttendanceData(new SelfAttendanceData(JSON.parse(selfAttendanceDataStore)));
    this.redirectURL = redirectURLStore;
  }

  setCurrentSelfAttendanceData(selfAttendanceData: SelfAttendanceData) {
    this.selfAttendanceData = selfAttendanceData;
    this.saveCurrentSelfAttendanceDataOnSessionStorage();
  }

  saveCurrentSelfAttendanceDataOnSessionStorage() {
    sessionStorage.setItem('selfAttendanceData', JSON.stringify(this.selfAttendanceData));
    localStorage.setItem('selfAttendanceData', JSON.stringify(this.selfAttendanceData));
    sessionStorage.setItem('redirectURL', this.redirectURL);
    localStorage.setItem('redirectURL', this.redirectURL);
  }

  public authenticate(productClassification: string, hashString: string, businessId: string, userId: string, fromCancellation: boolean): Observable<any> {
    return this.httpService.post('eCommercePME/eCommerceAPI/validateAuthToken', Api.One_Portal, {
      'authToken': hashString,
      'businessId': businessId,
      'userId': userId,
      'productClassification': productClassification,
      'fromCancellation': fromCancellation
    });
  }

  public getCancelContractData(userId: string, businessId: string, productClassification?: string): Observable<any> {
    return this.httpService.post('eCommercePME/eCommerceAPI/getCancelContractData', Api.One_Portal, {
      'userId': userId,
      'businessId': businessId,
      "productClassification": productClassification || null
    });
  }

  public cancelContract(businessId: string, userId: string, reason: string,
    message?: string, accountBankData?: any, classificationIdentifier?: string, timeStamp?: string): Observable<SelfAttendanceData> {
    return this.httpService.post('serasa/portalAPI/cancelContract', Api.One_Portal, {
      'businessId': businessId,
      'userId': userId,
      'reason': reason,
      'message': message,
      'accountBankData': accountBankData,
      'productClassification': classificationIdentifier,
      'timeStamp': timeStamp
    }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public getChangePaymentMethodDiscount(businessId: string, userId: string, productClassification: string, productId: string): Observable<any> {
    return this.httpService.post('serasa/portalAPI/getChangePaymentMethodDiscount', Api.One_Portal, {
      'userId': userId,
      'businessId': businessId,
      'productClassification': productClassification,
      'productId': productId,
    });
  }

  public getBankBillet2ndCopy(businessId: string, userId: string, classificationIdentifier: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getBankBillet2ndCopy', Api.One_Portal, {
      businessId: businessId,
      userId: userId,
      productClassification: classificationIdentifier
    }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public getPix2ndCopy(businessId: string, userId: string, classificationIdentifier: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getPix2ndCopy', Api.One_Portal, {
      businessId: businessId,
      userId: userId,
      productClassification: classificationIdentifier
    }).pipe(map(result => new SelfAttendanceData(result)));
  }
  public getRemittanceStatus(txId: string): Observable<any> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getPixRemittenceStatus', Api.One_Portal, {
      txId: txId
    });
  }

  public getBusinessPaymentMethod(businessId: string, userId: string, classificationIdentifier: string): Observable<any> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getBusinessPaymentMethod', Api.One_Portal, {
      'businessId': businessId,
      'userId': userId,
      'productClassification': classificationIdentifier
    });
  }

  public getPaymentChangeData(businessId: string, userId: string, classificationIdentifier: string, service?: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getPaymentChangeData', Api.One_Portal, {
      businessId: businessId,
      userId: userId,
      service: service,
      productClassification: classificationIdentifier
    }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public changePaymentMethod(paymentData: any): Observable<SelfAttendanceData> {

    if (paymentData.paymentMethodIdentifier === 'CREDIT_CARD') {
      paymentData.paymentToken = this.silentOrderService.paymentToken;
    }

    return this.httpService.post('serasa/portalAPI/changePaymentMethod', Api.One_Portal, paymentData).
      pipe(map(result => new SelfAttendanceData(result)));
  }

  public getPlansAvailableForUpgrade(userId: string, businessId: string, paymentMethod): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/eCommerceAPI/getUpgradeProducts', Api.One_Portal, {
      'userId': userId,
      'businessId': businessId,
      'paymentMethod': paymentMethod
    }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public changeContractPlan(params: any): Observable<SelfAttendanceData> {
    if (params.paymentData && params.paymentData.identifier === 'CREDIT_CARD') {
      params.paymentData.card.paymentToken = this.silentOrderService.paymentToken;
      params.paymentData.card.cardNumber = this.maskCreditCardNumber(params.paymentData.card.cardNumber);
    }

    return this.httpService.post('eCommercePME/eCommerceAPI/changePlan', Api.One_Portal, params).
      pipe(map(result => new SelfAttendanceData(result)));
  }

  private maskCreditCardNumber(cardNumber: string): string {
    const unmaskedValue = cardNumber.substr(cardNumber.length - 4, 4);
    let maskedValue = cardNumber.substr(0, cardNumber.length - 4);
    maskedValue = maskedValue.split(' ').map(
      digitGroup => {
        let maskedGroup = '';
        while (maskedGroup.length < digitGroup.length) {
          maskedGroup += '*';
        }
        return maskedGroup;
      }
    ).join(' ');
    return maskedValue + unmaskedValue;
  }

  public getActivateAutomaticRechargeData(businessId: string, userId: string, hash?: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getActivateAutomaticRechargeData', Api.One_Portal,
      {
        userId: userId,
        businessId: businessId,
        hash: hash
      }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public getChangeAutomaticRechargeData(businessId: string, userId: string, typeWindow: string, hash?: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getChangeAutomaticRechargeData', Api.One_Portal,
      {
        userId: userId,
        businessId: businessId,
        typeWindow: typeWindow,
        hash: hash
      }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public getDisableAutomaticRechargeData(businessId: string, userId: string, hash?: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getDisableAutomaticRechargeData', Api.One_Portal,
      {
        userId: userId,
        businessId: businessId,
        hash: hash
      }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public getAutomaticRechargePaymentChangeData(businessId: string, userId: string, hash?: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/getAutomaticRechargePaymentChangeData', Api.One_Portal,
      {
        userId: userId,
        businessId: businessId,
        hash: hash
      }).pipe(map(result => new SelfAttendanceData(result)));
  }

  public changeAutomaticRecharge(userId: string, businessId: string, productId: string, customValue?: Number): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/updateConsultaWallet', Api.One_Portal,
      {
        'productId': productId,
        'userId': userId,
        'businessId': businessId,
        'action': 'CHANGE',
        'customValue': customValue
      }).pipe(map(result => new SelfAttendanceData(result)))
  }

  public activateAutomaticRecharge(paymentData: any): Observable<SelfAttendanceData> {

    if (paymentData.paymentMethodIdentifier === 'CREDIT_CARD' && !paymentData.isAdyen) {
      paymentData.paymentToken = this.silentOrderService.paymentToken;
    }

    return this.httpService.post('eCommercePME/selfAttendanceAPI/updateConsultaWallet', Api.One_Portal,
      paymentData).pipe(map(result => new SelfAttendanceData(result)))
  }

  public disableAutomaticRecharge(userId: string, businessId: string): Observable<SelfAttendanceData> {
    return this.httpService.post('eCommercePME/selfAttendanceAPI/updateConsultaWallet', Api.One_Portal,
      {
        'userId': userId,
        'businessId': businessId,
        'action': 'DEACTIVATE'
      }).pipe(map(result => new SelfAttendanceData(result)))
  }

  public getStoredCreditCard(userId: string, businessId: string): Observable<CreditCardData | null> {
    return this.httpService.post('com.serasa.customer/pmeAccount/getStoredCreditCard', Api.One_Portal,
      {
        'userId': userId,
        'businessId': businessId,
        'basicInformation': true
      }).pipe(map(result => {
        if (!result) return null;
        return new CreditCardData(result);
      }));
  }

  public getStoredCreditCards(userId: string, businessId: string, hashString: string): Observable<[CreditCardData[], boolean, SuggestionMessage] | null> {
    return this.httpService.post('com.serasa.customer/storedCards/getStoredCreditCards', Api.One_Portal,
      {
        'userId': userId,
        'businessId': businessId,
        'hashString': hashString
      }).pipe(map(results => {

        if (!results || !Array.isArray(results.storedCardsList)) return null;

        const suggestionMessage = results.suggestionMessage

        const mappedResults = results.storedCardsList.map(storedCard => {
          return new CreditCardData(storedCard);
        });
        const maxLimitCreditCards = results.maxLimitCreditCards;

        results = [mappedResults, maxLimitCreditCards, suggestionMessage];

        return results;
      }));
  }

  public removeCreditCard(userId: string, businessId: string, accountPaymentDataId: string): Observable<[CreditCardData[], boolean] | null> {
    return this.httpService.post('com.serasa.customer/storedCards/removeCreditCard', Api.One_Portal,
      {
        'userId': userId,
        'businessId': businessId,
        'accountPaymentDataId': accountPaymentDataId
      }).pipe(map(results => {

        if (!results || !Array.isArray(results.storedCardsList)) return null;

        const mappedResults = results.storedCardsList.map(storedCard => {
          return new CreditCardData(storedCard);
        });
        const maxLimitCreditCards = results.maxLimitCreditCards;

        results = [mappedResults, maxLimitCreditCards];

        return results;
      }));
  }

  // public getProductBenefits(classificationIdentifier: string) {
  //   return this.httpService.post('serasa/productBenefits/getProductBenefits', Api.One_Portal, {
  //     "productClassification": classificationIdentifier,
  //   });
  // }

  public getConfigByPlan(planIdentifier: string, userId: string, businessId: string) {
    return this.httpService.post('serasa/cancellationRetentionConfig/getConfigByPlan', Api.One_Portal, {
      "productIdentifier": planIdentifier,
      "userId": userId,
      "businessId": businessId
    });
  }

  public setRetentionWasDisplayed(planIdentifier: string, userId: string, businessId: string, proceededWithCancellation: boolean, cancellationReason: string) {
    return this.httpService.post('serasa/cancellationRetentionConfig/setRetentionWasDisplayed', Api.One_Portal, {
      "productIdentifier": planIdentifier,
      "userId": userId,
      "businessId": businessId,
      "proceededWithCancellation": proceededWithCancellation,
      "cancellationReason": cancellationReason
    });
  }

  public renewPlanWithDiscount(cancelContractId: string, offerDiscountId: string) {
    return this.httpService.post('eCommercePME/eCommerceAPI/renewPlanWithDiscount', Api.One_Portal, {
      "contractId": cancelContractId,
      "offerDiscountId": offerDiscountId
    });
  }
}
